import { useEffect, useRef } from "react";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./ScholarshipSteps.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const STEPS = [
  {
    title: "scholarship.v2.steps.step1.title",
    description: "scholarship.v2.steps.step1.description",
  },
  {
    title: "scholarship.v2.steps.step2.title",
    description: "scholarship.v2.steps.step2.description",
  },
  {
    title: "scholarship.v2.steps.step3.title",
    description: "scholarship.v2.steps.step3.description",
  },
];

// The fill runs 0 → 1 as the rail travels between these two points down the
// viewport, expressed as a fraction of viewport height.
//
// The span between them is the whole animation's scroll budget: 1.05 → 0.2 is
// 0.85 of a viewport, so the progression reads at a normal scrolling pace. An
// earlier 0.85 → 0.35 gave it only half a viewport and the three steps lit in a
// flick. Starting just past 1.0 means it begins the moment the rail appears from
// below, and it still completes on step 3 well before the section leaves — no
// pinning, the reader keeps control of the scroll.
const START = 1.05;
const END = 0.2;

/** Sums an element's layout offset left up to an ancestor. */
const offsetLeftWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let x = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    x += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return x;
};

const offsetTopWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return y;
};

/**
 * Below this width the rail is VERTICAL — a line down the left with the chips on
 * it, matching the Group Booking stepper — so the progress runs top-to-bottom and
 * the chips are reached in order of their `offsetTop`, not their `offsetLeft`.
 *
 * Kept in sync with `@include below(md)` in the stylesheet by hand; there is no
 * way to read a Sass breakpoint from here. 899.98 is what `below(md)` compiles to.
 */
const VERTICAL_RAIL = "(max-width: 899.98px)";

/**
 * How to apply — three steps on a horizontal timeline whose rule fills as you
 * scroll, lighting each numbered chip as the fill passes it.
 *
 * Nothing is pinned: the progress is mapped to the section's own travel down the
 * viewport, so it finishes on step 3 and the page keeps scrolling normally.
 *
 * Degrades to a plain grey rule without JS, and shows the timeline complete for
 * reduced-motion users.
 */
const Steps = () => {
  const { t } = useTranslationHook();
  const railRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const rail = railRef.current;
    const fill = fillRef.current;
    if (!rail || !fill) return undefined;

    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let chips: HTMLElement[] = [];
    let centres: number[] = [];
    // The rail's extent along whichever axis it currently runs.
    let railSize = 0;
    let lit = -1;

    const measure = () => {
      chips = Array.from(rail.querySelectorAll<HTMLElement>("[data-step-chip]"));
      const vertical = window.matchMedia(VERTICAL_RAIL).matches;
      railSize = vertical ? rail.clientHeight : rail.clientWidth;
      // offsetLeft/offsetTop rather than rects: layout-based, so unaffected by the
      // Reveal transform on the heading block or any in-flight animation.
      centres = chips.map((c) =>
        vertical
          ? offsetTopWithin(c, rail) + c.offsetHeight / 2
          : offsetLeftWithin(c, rail) + c.offsetWidth / 2,
      );
    };

    const paint = (count: number) => {
      if (count === lit) return;
      lit = count;
      chips.forEach((chip, i) => {
        chip.classList.toggle(styles.chipActive, i < count);
      });
    };

    const update = () => {
      if (!railSize) return;
      const { top } = rail.getBoundingClientRect();
      const start = window.innerHeight * START;
      const end = window.innerHeight * END;
      const progress = Math.min(1, Math.max(0, (start - top) / (start - end)));

      // A NUMBER, not a transform. The stylesheet decides the axis — scaleX while
      // the rail is horizontal, scaleY once it is vertical — so the two can never
      // disagree about which way the fill grows.
      fill.style.setProperty("--progress", progress.toFixed(4));

      // Step 1's chip sits at the rail's very start, so it needs progress to have
      // actually begun before it counts as reached.
      const edge = progress * railSize;
      paint(progress > 0 ? centres.filter((c) => c <= edge + 0.5).length : 0);
    };

    measure();

    if (reduced) {
      fill.style.setProperty("--progress", "1");
      paint(chips.length);
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    let raf = 0;
    const onScroll = () => {
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          update();
        });
      }
    };
    const onResize = () => {
      measure();
      update();
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Reveal className={styles.head}>
          <h2 className={styles.heading}>
            {t("scholarship.v2.steps.heading")}
            <br />
            <span className={styles.headingMuted}>{t("scholarship.v2.steps.headingMuted")}</span>
          </h2>
        </Reveal>

        <div ref={railRef} className={styles.rail}>
          {/* Track, with the dark progress fill scaling out of its left edge. */}
          <span className={styles.rule} aria-hidden="true">
            <span ref={fillRef} className={styles.ruleFill} />
          </span>

          <div className={styles.cols}>
            {STEPS.map((step, i) => (
              <div key={step.title} className={styles.col}>
                <span className={styles.chip} data-step-chip>
                  {i + 1}
                </span>
                {/* h3, not a span: each column is a named step under this section's
                    h2, so it belongs in the page outline. Purely a markup change —
                    .col is a flex column, so a block-level child lays out the same. */}
                <h3 className={styles.colTitle}>{t(step.title)}</h3>
                <span className={styles.colText}>{t(step.description)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(Steps, {
  componentName: "Steps-ScholarshipV2",
  showForChina: true,
});
